<template>
    <div class="ds-schedule-page">
        <!-- Ширина тулбара — по viewport: ширина canvas растягивала его за экран (у Toolbar несколько корней, style ранее не применялся). -->
        <Toolbar />
        <div ref="scheduleViewport" class="ds-schedule-viewport">
            <div class="schedule-scroll-content">
            <div class="ds-schedule-canvas" :class="{ 'ds-week-canvas': isWeekLayout, 'ds-month-canvas': !isWeekLayout, 'ds-timesheet-canvas': isTimesheet }" :style="canvasStyle">
              <!-- Неделя (RD-137) и месяц (RD-138) — общие для графика и табеля (RD-139); различаются строки и колонка показателей. -->
              <template v-if="isWeekLayout">
                <div class="sticky top-0 z-40">
                    <div class="ds-week-header-row">
                        <div class="ds-week-lead-cell ds-week-header-lead">
                            <div class="ds-week-header-tools">
                                <div class="schedule-search">
                                    <SearchInput v-model="searchQuery" @clear="searchQuery = ''" />
                                </div>
                                <dropdown-radio :options="sortOptions" v-model="userSort" title="Сортировка" @change="onUserSort">
                                    <button-icon class="bg-white text-gray hover:text-darkblue" width="17" height="17">
                                        <IconFilter />
                                    </button-icon>
                                </dropdown-radio>
                            </div>
                            <div class="ds-week-metrics-cell ds-week-header-metrics">
                                <template v-if="store.isEmployeeStatsVisible && isTimesheet">
                                    <!-- Табель: вместо выбираемых показателей — месячные итоги сотрудника. -->
                                    <span class="ds-timesheet-total-main">осн.</span>
                                    <span class="ds-timesheet-total-additional">доп.</span>
                                    <span class="ds-timesheet-total-fact">итого</span>
                                </template>
                                <template v-else-if="store.isEmployeeStatsVisible">
                                    <div class="ds-week-metric-a relative tooltip-trigger">
                                        <dropdown-radio :options="userStatOptions" :show-tooltip="true" @change="onUserStat"
                                            :title="userStatMenuTitle('Первый показатель', userStat)"
                                            v-model="userStat">{{ userStat?.abbr }}</dropdown-radio>
                                    </div>
                                    <div class="ds-week-metric-b relative tooltip-trigger">
                                        <dropdown-radio :options="userStatOptions" :show-tooltip="true" @change="onUserStat"
                                            :title="userStatMenuTitle('Второй показатель', userStat2)"
                                            v-model="userStat2">{{ userStat2?.abbr }}</dropdown-radio>
                                    </div>
                                </template>
                            </div>
                        </div>
                        <div class="ds-week-days">
                            <div v-for="day in weekHeaderDays" :key="day.date" class="ds-week-header-cell"
                                :class="{ 'ds-week-header-cell-holiday': day.isHoliday, 'ds-week-header-cell-today': day.isToday }">
                                <span class="ds-week-header-date">{{ day.title }}</span>
                                <span class="ds-week-header-weekday">{{ day.weekday }}</span>
                                <div v-if="day.isToday" class="ds-week-cell-today" aria-hidden="true" />
                            </div>
                        </div>
                    </div>
                </div>

                <div class="ds-schedule-weeks-row">
                    <div class="ds-schedule-weeks-spacer"></div>
                    <div class="ds-week-number">{{ store.currentWeekStart.isoWeek() }}</div>
                </div>

                <!-- Вакансии в табель не добавляются. -->
                <ScheduleVacancies
                    v-if="!isTimesheet && authStore.permissions?.can_view_vacancies"
                    ref="scheduleVacancies"
                    :days="weekDates.length"
                    :dates="weekDates"
                    :today
                    @create="openVacancyPanel"
                />

                <div v-if="store.isDayStatsVisible" class="ds-week-day-stats-row">
                    <div class="ds-week-lead-cell">
                        <div class="ds-week-day-stats-titles">
                            <div class="ds-week-metric-a">
                                <dropdown-radio :options="dayStatOptions" @change="onDayStat" v-model="dayStat">{{ cut(dayStat?.title, 29) }}</dropdown-radio>
                            </div>
                            <div class="ds-week-metric-b">
                                <dropdown-radio :options="dayStatOptions" @change="onDayStat" v-model="dayStat2">{{ cut(dayStat2?.title, 29) }}</dropdown-radio>
                            </div>
                        </div>
                        <div class="ds-week-metrics-cell ds-week-metrics-cell-strong">
                            <span class="ds-week-metric-a">{{ formatDayStatValue(weekStatSum) }}</span>
                            <span class="ds-week-metric-b">{{ formatDayStatValue(weekStatSum2) }}</span>
                        </div>
                    </div>
                    <div class="ds-week-days">
                        <div v-for="(date, index) in weekDates" :key="date" class="ds-week-day-stats-cell">
                            <span class="ds-week-metric-a">{{ formatDayStatValue(weekDayStats[index]) }}</span>
                            <span class="ds-week-metric-b">{{ formatDayStatValue(weekDayStats2[index]) }}</span>
                            <div v-if="date === todayDate" class="ds-week-cell-today" aria-hidden="true" />
                        </div>
                    </div>
                </div>

                <TimeSheetTable v-if="isTimesheet" :dates="weekDates" layout="week" :schedules="filteredSchedules" @day-clicked="editTimesheet($event)"
                    @user-clicked="openUserPanel($event)" />
                <ScheduleTable v-else :days :today :dates="weekDates" :schedules="filteredSchedules" @day-clicked="openSidePanel($event)"
                    @user-clicked="openUserPanel($event)" :key="store.refresh" />
              </template>
              <!-- Месяц (RD-138): структура и колонки недели, дни 41–56 px. -->
              <template v-else>
                <div class="sticky top-0 z-40">
                    <div class="ds-week-header-row">
                        <div class="ds-week-lead-cell ds-week-header-lead">
                            <div class="ds-week-header-tools">
                                <div class="schedule-search">
                                    <SearchInput v-model="searchQuery" @clear="searchQuery = ''" />
                                </div>
                                <dropdown-radio :options="sortOptions" v-model="userSort" title="Сортировка" @change="onUserSort">
                                    <button-icon class="bg-white text-gray hover:text-darkblue" width="17" height="17">
                                        <IconFilter />
                                    </button-icon>
                                </dropdown-radio>
                            </div>
                            <div class="ds-week-metrics-cell ds-week-header-metrics">
                                <template v-if="store.isEmployeeStatsVisible && isTimesheet">
                                    <!-- Табель: вместо выбираемых показателей — месячные итоги сотрудника. -->
                                    <span class="ds-timesheet-total-main">осн.</span>
                                    <span class="ds-timesheet-total-additional">доп.</span>
                                    <span class="ds-timesheet-total-fact">итого</span>
                                </template>
                                <template v-else-if="store.isEmployeeStatsVisible">
                                    <div class="ds-week-metric-a relative tooltip-trigger">
                                        <dropdown-radio :options="userStatOptions" :show-tooltip="true" @change="onUserStat"
                                            :title="userStatMenuTitle('Первый показатель', userStat)"
                                            v-model="userStat">{{ userStat?.abbr }}</dropdown-radio>
                                    </div>
                                    <div class="ds-week-metric-b relative tooltip-trigger">
                                        <dropdown-radio :options="userStatOptions" :show-tooltip="true" @change="onUserStat"
                                            :title="userStatMenuTitle('Второй показатель', userStat2)"
                                            v-model="userStat2">{{ userStat2?.abbr }}</dropdown-radio>
                                    </div>
                                </template>
                            </div>
                        </div>
                        <div class="ds-week-days">
                            <div v-for="day in monthHeaderDays" :key="day.date" class="ds-week-header-cell"
                                :class="{ 'ds-week-header-cell-holiday': day.isHoliday, 'ds-week-header-cell-today': day.isToday }">
                                <div class="ds-month-header-day relative schedule-day-tooltip-trigger" tabindex="0">
                                    <span class="ds-week-header-date">{{ day.title }}</span>
                                    <span class="ds-week-header-weekday">{{ day.weekday }}</span>
                                    <Tooltip :text="day.note" placement="bottom" />
                                </div>
                                <div v-if="day.isToday" class="ds-week-cell-today" aria-hidden="true" />
                            </div>
                        </div>
                    </div>
                </div>

                <div class="ds-schedule-weeks-row">
                    <div class="ds-schedule-weeks-spacer"></div>
                    <div class="ds-month-weeks">
                        <div v-for="week in monthWeekSegments" :key="week.start" class="ds-month-week"
                            :style="{ flexGrow: week.days, flexShrink: week.days }">
                            {{ week.number }}
                        </div>
                    </div>
                </div>

                <ScheduleVacancies
                    v-if="!isTimesheet && authStore.permissions?.can_view_vacancies"
                    ref="scheduleVacancies"
                    :days
                    :today
                    @create="openVacancyPanel"
                />

                <div v-if="store.isDayStatsVisible" class="ds-week-day-stats-row">
                    <div class="ds-week-lead-cell">
                        <div class="ds-week-day-stats-titles">
                            <div class="ds-week-metric-a">
                                <dropdown-radio :options="dayStatOptions" @change="onDayStat" v-model="dayStat">{{ cut(dayStat?.title, 29) }}</dropdown-radio>
                            </div>
                            <div class="ds-week-metric-b">
                                <dropdown-radio :options="dayStatOptions" @change="onDayStat" v-model="dayStat2">{{ cut(dayStat2?.title, 29) }}</dropdown-radio>
                            </div>
                        </div>
                        <div class="ds-week-metrics-cell">
                            <span class="ds-week-metric-a">{{ formatDayStatValue(statSum) }}</span>
                            <span class="ds-week-metric-b">{{ formatDayStatValue(statSum2) }}</span>
                        </div>
                    </div>
                    <div class="ds-week-days">
                        <div v-for="day in monthHeaderDays" :key="day.date" class="ds-week-day-stats-cell">
                            <span class="ds-week-metric-a">{{ formatDayStatValue(dayStats[day.day]) }}</span>
                            <span class="ds-week-metric-b">{{ formatDayStatValue(dayStats2[day.day]) }}</span>
                            <div v-if="day.isToday" class="ds-week-cell-today" aria-hidden="true" />
                        </div>
                    </div>
                </div>

                <TimeSheetTable v-if="isTimesheet" :dates="monthDates" layout="month" :schedules="filteredSchedules" @day-clicked="editTimesheet($event)"
                    @user-clicked="openUserPanel($event)" />
                <ScheduleTable v-else :days :today :dates="monthDates" layout="month" :schedules="filteredSchedules" @day-clicked="openSidePanel($event)"
                    @user-clicked="openUserPanel($event)" :key="store.refresh" />
              </template>
            </div>
            <div
                v-if="sidebarSpacerWidth"
                class="shrink-0"
                :style="{ width: `${sidebarSpacerWidth}px` }"
                aria-hidden="true"
            />
            </div>
        </div>
        <div v-if="switcher" class="ds-schedule-statbar-shell">
            <div class="ds-schedule-canvas">
                <StatBar />
            </div>
        </div>
    </div>
    <SidePanel ref="sidebar" :locked="isShiftEditorBusy" @close="restoreScheduleScroll" @opened="scrollEditedDayIntoView">
        <div v-if="loading" class="flex items-center h-full justify-center">
            <Loader />
        </div>
        <ShiftInfo ref="shiftInfo" v-else :scheduleIndex="scheduleIndex" :day="workday" :date="workdate" @close="closeSidePanel" @switch="onShiftSwitch" />
    </SidePanel>

    <!-- Досье C (RD-149): только выбранное ТУ подразделения графика, без меню и изменений. -->
    <SidePanel ref="usercard">
        <EmployeeDossier v-if="userSchedule" :key="userSchedule.id" context="schedule" :schedule="userSchedule" />
    </SidePanel>

    <SidePanel ref="vacancySidebar">
        <AddVacancy
            :initial-values="vacancyInitialValues"
            @close="closeVacancyPanel"
            @saved="reloadVacancies"
        />
    </SidePanel>

    <modal v-if="showModal" @close="!isSavingCurrentShift && cancelShiftSwitch()">
        <div class="w-[332px]">
            <div class="ds-glass-dialog-title">Сохранить изменения?</div>
            <div class="ds-glass-dialog-text">{{ unsavedShiftText }}</div>
            <div class="ds-glass-dialog-actions">
                <button class="ds-glass-btn ds-glass-btn-ghost" type="button" :disabled="isSavingCurrentShift" @click="cancelShiftSwitch">Остаться</button>
                <button class="ds-glass-btn ds-glass-btn-secondary" type="button" :disabled="isSavingCurrentShift" @click="discardCurrentShift">Не сохранять</button>
                <button class="ds-glass-btn ds-glass-btn-primary" type="button" :disabled="isSavingCurrentShift" @click="saveCurrentShift">Сохранить</button>
            </div>
        </div>
    </modal>

</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, computed, nextTick, onBeforeUpdate, watch } from 'vue'
import Toolbar from '../components/header/Toolbar.vue'
import IconFilter from '../ui/icons/IconFilter.vue'
import dropdownRadio from '../components/dropdownRadio.vue'
import Tooltip from '../components/Tooltip.vue'
import StatBar from '../components/schedule/StatBar.vue'
import SidePanel from '../components/schedule/SidePanel.vue'
import ShiftInfo from '../components/schedule/ShiftInfo.vue'
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import isoWeek from 'dayjs/plugin/isoWeek'
import ScheduleTable from '../components/schedule/ScheduleTable.vue'
import ScheduleVacancies from '@/components/schedule/ScheduleVacancies.vue'
import EmployeeDossier from '@/components/dossier/EmployeeDossier.vue'
import TimeSheetTable from '@/components/timesheet/TimeSheetTable.vue'
import Loader from '@/ui/Loader.vue'
import { useAuthStore } from '@/stores/authStore'
import { useScheduleStore } from '@/stores/scheduleStore'
import AddVacancy from '@/components/exchange/AddVacancy.vue'
import SearchInput from '@/components/SearchInput.vue'
import type { Option, Schedule } from '@/types'
import { DATE_FORMAT, shiftTime } from '@/components/schedule/weekSchedule'
import { ownRowShifts } from '@/utils/otherDepartmentShifts'

dayjs.locale('ru')
dayjs.extend(utc)
dayjs.extend(isoWeek)

const store = useScheduleStore()
const authStore = useAuthStore()

const stats = computed(() => store.stats)

const userStatOptions = computed(() => {
    const options: Array<{ title: string, value: string, abbr: string, description?: string }> = []
    if (store.stats) {
        const stats = store.stats
        for (let stat in stats) {
            options.push({
                title: stats[stat].name,
                value: stat,
                abbr: stats[stat].abbreviation,
                description: stats[stat].description
            })
        }
        return options
    }
    return []
})

const dayStatOptions = [
    { title: 'Часов по плану', value: 'plan-hours' },
    { title: 'Часы собственных сотрудников', value: 'employee-hours' },
    { title: 'Часы внешних сотрудников', value: 'outsource-hours' },
    { title: 'Количество работающих сотрудников', value: 'employees' }
]

const sortOptions = [
    { title: 'По имени', value: 'name' },
    { title: 'По должности', value: 'position' }
]

const userSort = ref(sortOptions[0])

const switcher = computed(() => !store.isTimesheetMode)
const isTimesheet = computed(() => store.isTimesheetMode)
// Вид периода общий для графика и табеля.
const isWeekLayout = computed(() => store.isWeekPeriod)
const searchQuery = ref('')
const userStat = ref()
const userStat2 = ref()
const dayStat = ref(dayStatOptions[0])
const dayStat2 = ref(dayStatOptions[0])
const statSum = ref(0)
const statSum2 = ref(0)
const scheduleIndex = ref(0)
const workday = ref()
const workdate = ref<string>()
const selectedScheduleCell = ref<HTMLElement | null>(null)
const loading = ref(false)
const dayStats = ref<any[]>([])
const dayStats2 = ref<any[]>([])
const showModal = ref(false)
const savedScheduleScrollLeft = ref<number | null>(null)
const sidebarSpacerWidth = ref(0)
let spacerResetTimer: number | undefined

const cut = (text: string, num: number) => text.slice(0, num)
// Заголовок меню показателя: колонка — текущее сокращение выбранного показателя.
const userStatMenuTitle = (label: string, stat?: { abbr?: string }) => stat?.abbr ? `${label} · в колонке «${stat.abbr}»` : label
// Текст подтверждения: реальный плановый интервал редактируемой смены.
const unsavedShiftText = computed(() => {
    const start = shiftTime(store.currentShift?.plan_work_start_at)
    const end = shiftTime(store.currentShift?.plan_work_end_at)
    const interval = start && end ? ` ${start.format('HH:mm')}–${end.format('HH:mm')}` : ''
    return `В смене${interval} есть несохранённые изменения.`
})
const formatDayStatValue = (value: unknown) => {
    const numeric = Number(value)
    if (!Number.isFinite(numeric)) return 0
    return Math.round(numeric)
}
const sidebar = ref<{ toggle: () => void; panel: HTMLElement | null }>()
const scheduleViewport = ref<HTMLElement | null>(null)
const usercard = ref()
const vacancySidebar = ref()
const scheduleVacancies = ref<{ reload: () => Promise<void> }>()
const userSchedule = ref<Schedule | null>(null)
const shiftInfo = ref()
// Копирование в редакторе (RD-140): пока идёт preview/execute, пользователь не закрывает панель.
const isShiftEditorBusy = computed(() => Boolean(shiftInfo.value?.isBusy))
const vacancyInitialValues = ref<{
    date: string
    department: Option
    inner: boolean
    outer: boolean
}>()

const resetShiftsChanges = () => {
    if (scheduleIndex.value >= 0 && scheduleIndex.value < store.schedules.length) {
        const targetSchedule = store.schedules[scheduleIndex.value];
        targetSchedule.shifts = targetSchedule.shifts.map(shift => {
            const replacement = store.originalShifts.find(os => os.id === shift.id);
            return replacement || shift;
        });
    }
    store.isCurrentShiftChanged = false
}

const openSidePanel = (data: any) => {
    const { day, date, index, cell } = data
    scheduleIndex.value = index
    workday.value = day
    workdate.value = date
    selectedScheduleCell.value = cell instanceof HTMLElement ? cell : null
    if(store.isCurrentShiftChanged) resetShiftsChanges()
    if (spacerResetTimer) window.clearTimeout(spacerResetTimer)
    sidebarSpacerWidth.value = 0
    const viewport = scheduleViewport.value
    savedScheduleScrollLeft.value = viewport?.scrollLeft ?? null
    sidebar.value?.toggle()
}

const scrollEditedDayIntoView = () => {
    const viewport = scheduleViewport.value
    const target = selectedScheduleCell.value
    const panel = sidebar.value?.panel
    if (!viewport || !target || !panel) return

    const getRequiredShift = () => {
        const targetRect = target.getBoundingClientRect()
        const sidebarRect = panel.getBoundingClientRect()
        const cellWidth = targetRect.width
        const hasNextDay = target.nextElementSibling !== null
        const requiredGap = hasNextDay ? cellWidth : 0

        return targetRect.right + requiredGap - sidebarRect.left
    }

    const scrollToContext = () => {
        const requiredShift = getRequiredShift()
        if (requiredShift <= 0) return

        const targetScrollLeft = viewport.scrollLeft + requiredShift
        const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth)
        if (targetScrollLeft > maxScroll) {
            sidebarSpacerWidth.value += targetScrollLeft - maxScroll
            nextTick(() => requestAnimationFrame(scrollToContext))
            return
        }

        viewport.scrollTo({ left: targetScrollLeft, behavior: 'smooth' })
    }

    scrollToContext()
}

const closeSidePanel = () => {
    sidebar.value?.toggle()
    restoreScheduleScroll()
}

const restoreScheduleScroll = () => {
    const viewport = scheduleViewport.value
    const scrollLeft = savedScheduleScrollLeft.value
    if (viewport && scrollLeft !== null) {
        viewport.scrollTo({ left: scrollLeft, behavior: 'smooth' })
    }
    savedScheduleScrollLeft.value = null
    if (spacerResetTimer) window.clearTimeout(spacerResetTimer)
    spacerResetTimer = window.setTimeout(() => {
        sidebarSpacerWidth.value = 0
    }, 350)
}

const openVacancyPanel = (data: { day: number; date?: string; channel: 'outer' | 'inner' }) => {
    const department = store.currentDepartment
    if (!department || !authStore.permissions?.can_create_vacancy_exchange) return

    vacancyInitialValues.value = {
        date: data.date ?? store.currentScheduleDate.date(data.day).format('YYYY-MM-DD'),
        department: { id: department.id, name: department.name },
        inner: data.channel === 'inner',
        outer: data.channel === 'outer',
    }
    vacancySidebar.value.toggle()
}

const closeVacancyPanel = () => {
    vacancySidebar.value.toggle()
}

const reloadVacancies = () => scheduleVacancies.value?.reload()

// Строка графика (id — Employment UUID); досье само запрашивает данные по правам.
const openUserPanel = (data: Schedule) => {
    userSchedule.value = data
    usercard.value.toggle()
}

const today = dayjs().format('D')

const days = computed(() => store.currentScheduleDate.daysInMonth())
const filteredSchedules = computed(() => {
    const query = searchQuery.value.trim().toLocaleLowerCase()
    if (!query) return store.schedules

    return store.schedules.filter((schedule: Schedule) => {
        const nameParts = [schedule.user?.last_name, schedule.user?.first_name, schedule.user?.middle_name]
            .filter(Boolean)
        const fullName = nameParts.join(' ').toLocaleLowerCase()
        const reverseFullName = [schedule.user?.first_name, schedule.user?.last_name, schedule.user?.middle_name]
            .filter(Boolean)
            .join(' ')
            .toLocaleLowerCase()
        const positionName = schedule.position?.name?.toLocaleLowerCase() ?? ''

        return fullName.includes(query) || reverseFullName.includes(query) || positionName.includes(query)
    })
})
const calendar = computed(() => store.prodCalendar)
const canvasStyle = computed(() => {
    // Неделя занимает ширину экрана: дни делят место после колонок сотрудника и показателей.
    // Минимум дня табеля меньше графика: в ячейке нет двух колонок смен (RD-139).
    if (isWeekLayout.value) {
        const width = viewportWidth.value ? `${viewportWidth.value}px` : '100%'
        return { width, minWidth: isTimesheet.value ? 'var(--schedule-timesheet-week-min-width)' : 'var(--schedule-week-min-width)' }
    }
    // Месяц: дни делят ширину экрана, но не уже 41 и не шире 56 px; при нехватке места — горизонтальный скролл.
    const minWidth = `calc(var(--schedule-week-lead-width) + ${days.value} * var(--schedule-month-day-min-width))`
    const maxWidth = `calc(var(--schedule-week-lead-width) + ${days.value} * var(--schedule-month-day-max-width))`
    const width = viewportWidth.value ? `clamp(${minWidth}, ${viewportWidth.value}px, ${maxWidth})` : minWidth
    return { width, minWidth }
})

const todayDate = dayjs().format(DATE_FORMAT)
const monthDates = computed(() => Array.from({ length: days.value }, (_, index) => (
    store.currentScheduleDate.date(index + 1).format(DATE_FORMAT)
)))
// Шапка месяца строится по датам месяца, производственный календарь только размечает выходные/праздники.
const monthHeaderDays = computed(() => monthDates.value.map((date, index) => {
    const calendarDay = calendar.value.find(item => dayjs(item.date).format(DATE_FORMAT) === date)
    const day = dayjs(date)
    return {
        date,
        day: index + 1,
        title: day.format('DD.MM'),
        weekday: day.format('dd').toUpperCase(),
        note: calendarDay?.note || calendarDay?.day_type_text,
        isHoliday: Boolean(calendarDay?.is_weekend || calendarDay?.is_holiday),
        isToday: date === todayDate,
    }
}))
// ISO-недели месяца: номер по центру видимого в месяце отрезка её дней.
const monthWeekSegments = computed(() => {
    const segments: { start: string, number: number, days: number }[] = []
    for (const date of monthDates.value) {
        const number = dayjs(date).isoWeek()
        const last = segments[segments.length - 1]
        if (last && last.number === number) last.days++
        else segments.push({ start: date, number, days: 1 })
    }
    return segments
})
const weekDates = computed(() => store.currentWeekDates)
const weekHeaderDays = computed(() => weekDates.value.map(date => {
    const calendarDay = calendar.value.find(item => dayjs(item.date).format(DATE_FORMAT) === date)
    const day = dayjs(date)
    return {
        date,
        title: day.format('D MMMM'),
        weekday: day.format('dddd'),
        isHoliday: Boolean(calendarDay?.is_weekend || calendarDay?.is_holiday),
        isToday: date === todayDate,
    }
}))
const weekDayStats = computed(() => weekDates.value.map(date => calcStatFor(dateMatcher(date), dayStat.value)))
const weekDayStats2 = computed(() => weekDates.value.map(date => calcStatFor(dateMatcher(date), dayStat2.value)))
const weekStatSum = computed(() => weekDayStats.value.reduce((sum, item) => sum + (item ?? 0), 0))
const weekStatSum2 = computed(() => weekDayStats2.value.reduce((sum, item) => sum + (item ?? 0), 0))

type ShiftMatcher = (shift: any) => boolean
const dayMatcher = (day: any): ShiftMatcher => (shift) => +dayjs(shift.date).format('D') == day
const dateMatcher = (date: string): ShiftMatcher => (shift) => dayjs(shift.date).isSame(date, 'day')

const calcStat = (day: any, stat: any) => calcStatFor(dayMatcher(day), stat)

function calcStatFor(matcher: ShiftMatcher, stat: any) {
    if (stat.value === 'plan-hours') {
        const schedules = store.schedules.filter(item => item.shifts.some(shift => shift.department === store.currentDepartment?.id))
        const shifts = dayShifts(schedules, matcher)
        if (shifts.length) {
            return shiftsHours(shifts)
        }
        return 0
    }
    if (stat.value === 'employee-hours') {
        const schedules = store.schedules.filter(item => item.department.id === store.currentDepartment?.id)
        const shifts = dayShifts(schedules, matcher)
        if (shifts.length) {
            return shiftsHours(shifts)
        }
        return 0
    }
    if (stat.value === 'outsource-hours') {
        const schedules = store.schedules.filter(item => item.department.id !== store.currentDepartment?.id)
        const shifts = dayShifts(schedules, matcher)
        if (shifts.length) {
            return shiftsHours(shifts)
        }
        return 0
    }
    if (stat.value === 'employees') {
        const shifts = dayShifts(store.schedules, matcher)
        return [... new Set(shifts.map((shift: any) => shift.employment))].length
    }
}

function onDayStat() {
    localStorage.setItem('dayStat', JSON.stringify(dayStat.value))
    localStorage.setItem('dayStat2', JSON.stringify(dayStat2.value))
    dayStats.value = []
    calcDayStats(dayStats.value, dayStat.value)
    statSum.value = dayStats.value.reduce((sum, item) => sum + item, 0)
    dayStats2.value = []
    calcDayStats(dayStats2.value, dayStat2.value)
    statSum2.value = dayStats2.value.reduce((sum, item) => sum + item, 0)
}

function calcDayStats(arrStats: any[], stat: any) {
    for (let day = 0; day <= days.value; day++) {
        arrStats.push(calcStat(day, stat))
    }
}

function shiftsHours(shifts: any) {
    return shifts.reduce((sum: any, item: any) => {
        return sum + getShiftDurationHours(item)
    }, 0)
}

function getShiftDurationHours(shift: any) {
    const duration = Number(shift?.duration)
    if (Number.isFinite(duration) && duration >= 0) return duration

    if (!shift?.plan_work_start_at || !shift?.plan_work_end_at) return 0

    const start = dayjs(shift.plan_work_start_at)
    let end = dayjs(shift.plan_work_end_at)

    if (!start.isValid() || !end.isValid()) return 0

    if (end.isSame(start) || end.isBefore(start)) {
        end = end.add(1, 'day')
    }

    return end.diff(start, 'minute') / 60
}

// Дневные показатели подразделения: смены в других подразделениях не учитываются (RD-176);
// каждая смена — только в строке своего ТУ, без удвоения из чужих строк (RD-182).
function dayShifts(schedules: any, matcher: ShiftMatcher) {
    return schedules.map((item: any) => ownRowShifts(item, store.currentDepartment?.id).filter(matcher)).flat()
}

function getDayStatsFromLocalStorage() {
    const ds = localStorage.getItem('dayStat')
    const ds2 = localStorage.getItem('dayStat2')
    if (ds) (dayStat.value = JSON.parse(ds))
    if (ds2) (dayStat2.value = JSON.parse(ds2))
}

function onUserStat() {
    store.userStat = userStat.value
    store.userStat2 = userStat2.value
    localStorage.setItem('userStat', JSON.stringify(userStat.value))
    localStorage.setItem('userStat2', JSON.stringify(userStat2.value))
}

function getUserStatsFromLocalStorage() {
    const options = userStatOptions.value
    const getStoredOption = (key: string) => {
        const stored = localStorage.getItem(key)
        if (!stored) return options[0]

        try {
            const value = JSON.parse(stored)?.value
            return options.find(option => option.value === value) ?? options[0]
        } catch {
            return options[0]
        }
    }

    userStat.value = getStoredOption('userStat')
    userStat2.value = getStoredOption('userStat2')
    store.userStat = userStat.value
    store.userStat2 = userStat2.value

    if (userStat.value) localStorage.setItem('userStat', JSON.stringify(userStat.value))
    else localStorage.removeItem('userStat')
    if (userStat2.value) localStorage.setItem('userStat2', JSON.stringify(userStat2.value))
    else localStorage.removeItem('userStat2')
}

const editTimesheet = (e: any) => {
    if (authStore.permissions?.can_edit_timesheet) openSidePanel(e)
}

const onUserSort = () => {
    if(userSort.value.value === 'position') {
        store.schedules = store.schedulesSortedByPosition
    } else {
        store.schedules = store.schedulesSortedByName
    }
    store.refresh++
}

const onShiftSwitch = () => {
    showModal.value = true
}

const discardCurrentShift = () => {
    loading.value = true
    // reset changes
    resetShiftsChanges()
    setTimeout(() => loading.value = false, 0)
    showModal.value = false
    shiftInfo.value?.completePendingShiftSwitch()
}

const cancelShiftSwitch = () => {
    showModal.value = false
    shiftInfo.value?.cancelPendingShiftSwitch()
}

// Пока идёт сохранение из окна, его действия недоступны: один submit, без перехода до результата.
const isSavingCurrentShift = ref(false)

const saveCurrentShift = async () => {
    if (isSavingCurrentShift.value) return
    if (!shiftInfo.value?.validateCurrentShiftPlanTime()) {
        showModal.value = false
        shiftInfo.value?.cancelPendingShiftSwitch()
        return
    }

    isSavingCurrentShift.value = true
    let savedShifts: Awaited<ReturnType<typeof store.shiftPackCreateUpdate>>
    try {
        savedShifts = await store.shiftPackCreateUpdate([store.currentShift])
    } finally {
        isSavingCurrentShift.value = false
    }
    // Отказ записи: исходная карточка, черновик и окно остаются; переход не выполняется.
    if (!savedShifts) return

    store.isCurrentShiftChanged = false
    store.isSaveShiftEnabled = false
    showModal.value = false
    shiftInfo.value?.completePendingShiftSwitch()
}

watch(stats, () => getUserStatsFromLocalStorage())

const viewportWidth = ref(0)
let viewportObserver: ResizeObserver | undefined

onMounted(async () => {
    getDayStatsFromLocalStorage()
    if (scheduleViewport.value) {
        viewportWidth.value = scheduleViewport.value.clientWidth
        viewportObserver = new ResizeObserver(() => {
            viewportWidth.value = scheduleViewport.value?.clientWidth ?? 0
        })
        viewportObserver.observe(scheduleViewport.value)
    }
})

onBeforeUnmount(() => {
    if (spacerResetTimer) window.clearTimeout(spacerResetTimer)
    viewportObserver?.disconnect()
    // При уходе со страницы возвращаемся к графику; вид и период сохраняются.
    store.setTimesheetMode(false)
})

onBeforeUpdate(() => {
    onDayStat()
})
</script>

<style scoped>
.schedule-scroll-content {
    display: flex;
    width: max-content;
    min-width: 100%;
}

.schedule-search {
    --search-width: 160px;
    --search-height: 24px;
    --search-icon-size: 15px;
    --search-bg: var(--color-bg-surface);
    --search-border: var(--color-bg-surface);
}

.schedule-search :deep(.ds-search-field > div) {
    width: var(--search-height);
    height: var(--search-height);
}

.schedule-search :deep(.ds-search-field > div svg) {
    width: var(--search-icon-size);
    height: var(--search-icon-size);
}

</style>
